import React, { useState, useEffect, useCallback } from 'react';
import { postService } from '../services/post.service';
import { Button, Input, Select } from '../../components/ui/index';
import type { Post, PostFilters, AnimalSpecies, PostStatus, PostReviewStatus } from '../types';
import { API_BASE_URL, DEFAULT_PAGE_SIZE } from '../constants';
import { TableSkeleton } from '../components/Skeleton';
import { Pagination } from '../components/Pagination';
import '../../globals.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getPostDisplayName(post?: Pick<Post, 'name' | 'type'> | null) {
  const name = String(post?.name || '').trim();
  if (name) return name;
  return post?.type === 'FOUND' ? 'Animal encontrado' : 'Animal perdido';
}

export default function Posts() {
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [species, setSpecies] = useState<AnimalSpecies[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [reviewEmails, setReviewEmails] = useState<string[]>([]);
  const [manualApprovalEnabled, setManualApprovalEnabled] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [decisionLoadingId, setDecisionLoadingId] = useState<number | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  const [filters, setFilters] = useState<PostFilters>({
    speciesId: undefined,
    city: '',
    region: '',
    startDate: '',
    endDate: '',
    status: undefined,
    reviewStatus: undefined,
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
  });

  const loadSpecies = useCallback(async () => {
    try {
      const data = await postService.getAllSpecies();
      setSpecies(data || []);
    } catch (err) {
      console.error('Erro ao carregar espécies:', err);
      setSpecies([]);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await postService.getAllPosts({ page: 1, limit: 1000 });
      setAllPosts(response.posts || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar publicações';
      setError(message);
      setAllPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReviewSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const response = await postService.getPostReviewSettings();
      setReviewEmails(Array.isArray(response.emails) ? response.emails : []);
      setManualApprovalEnabled(response.manualApprovalEnabled ?? true);
    } catch (err) {
      console.error('Erro ao carregar emails de revisão:', err);
      setSettingsMessage('Não foi possível carregar os e-mails de notificação.');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSpecies();
    loadReviewSettings();
  }, [loadSpecies, loadReviewSettings]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    let filtered = [...allPosts];

    if (filters.speciesId) {
      filtered = filtered.filter((post) => {
        return post.speciesId === filters.speciesId ||
               post.animalSpecies?.id === filters.speciesId;
      });
    }

    if (filters.region) {
      const searchRegion = filters.region.toLowerCase();
      filtered = filtered.filter((post) =>
        post.locationRegion?.toLowerCase().includes(searchRegion)
      );
    }

    if (filters.city) {
      const searchCity = filters.city.toLowerCase();
      filtered = filtered.filter((post) =>
        post.locationCity?.toLowerCase().includes(searchCity)
      );
    }

    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter((post) => new Date(post.createdAt) >= startDate);
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((post) => new Date(post.createdAt) <= endDate);
    }

    if (filters.status) {
      filtered = filtered.filter((post) => post.status === filters.status);
    }

    if (filters.reviewStatus) {
      filtered = filtered.filter((post) => normalizeReviewStatus(post) === filters.reviewStatus);
    }

    setTotal(filtered.length);
    const pages = Math.ceil(filtered.length / (filters.limit || DEFAULT_PAGE_SIZE));
    setTotalPages(pages || 1);

    const startIndex = ((filters.page || 1) - 1) * (filters.limit || DEFAULT_PAGE_SIZE);
    const endIndex = startIndex + (filters.limit || DEFAULT_PAGE_SIZE);
    setPosts(filtered.slice(startIndex, endIndex));
  }, [allPosts, filters]);

  const normalizeReviewStatus = (post: Post): PostReviewStatus => {
    return (post.reviewStatus || 'APPROVED') as PostReviewStatus;
  };

  const handleSearch = () => {
    setFilters({ ...filters, page: 1 });
  };

  const handleClearFilters = () => {
    setFilters({
      speciesId: undefined,
      city: '',
      region: '',
      startDate: '',
      endDate: '',
      status: undefined,
      reviewStatus: undefined,
      page: 1,
      limit: DEFAULT_PAGE_SIZE,
    });
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPostTypeLabel = (type?: string) => {
    if (type === 'FOUND') return 'Encontrado';
    return 'Perdido';
  };

  const getSexLabel = (sex?: number) => {
    if (sex === 1) return 'Macho';
    if (sex === 2) return 'Fêmea';
    return 'Não identificado';
  };

  const getImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const base = API_BASE_URL.replace(/\/$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  };

  const getVisibilityBadge = (status: PostStatus) => {
    const isActive = status === 'ACTIVE';
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        background: isActive ? 'var(--success-bg)' : 'var(--muted)',
        color: isActive ? 'var(--success)' : 'var(--muted-foreground)',
        whiteSpace: 'nowrap',
      }}>
        {isActive ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  const getReviewBadge = (reviewStatus: PostReviewStatus) => {
    const config = {
      PENDING: {
        label: 'Em revisão',
        background: '#fff8e1',
        color: '#9a6700',
      },
      APPROVED: {
        label: 'Aprovado',
        background: '#e8f5e9',
        color: '#256029',
      },
      REJECTED: {
        label: 'Recusado',
        background: '#ffeaea',
        color: '#b42318',
      },
    }[reviewStatus];

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        background: config.background,
        color: config.color,
        whiteSpace: 'nowrap',
      }}>
        {config.label}
      </span>
    );
  };

  const addEmailToList = () => {
    const email = emailInput.trim().toLowerCase();

    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      setSettingsMessage('Informe um e-mail válido antes de adicionar.');
      return;
    }

    if (reviewEmails.includes(email)) {
      setSettingsMessage('Esse e-mail já está na lista.');
      return;
    }

    setReviewEmails((prev) => [...prev, email]);
    setEmailInput('');
    setSettingsMessage(null);
  };

  const removeEmailFromList = (emailToRemove: string) => {
    setReviewEmails((prev) => prev.filter((email) => email !== emailToRemove));
    setSettingsMessage(null);
  };

  const saveReviewSettings = async () => {
    try {
      setSettingsSaving(true);
      const response = await postService.updatePostReviewSettings({
        emails: reviewEmails,
        manualApprovalEnabled,
      });
      setReviewEmails(Array.isArray(response.emails) ? response.emails : []);
      setManualApprovalEnabled(response.manualApprovalEnabled ?? true);
      setSettingsMessage('Configuração salva com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar configuração';
      setSettingsMessage(message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleReviewDecision = async (post: Post, reviewStatus: 'APPROVED' | 'REJECTED') => {
    try {
      setDecisionLoadingId(post.id);
      setError(null);

      const updated = await postService.updatePostReviewStatus(post.id, reviewStatus);
      setAllPosts((prev) => prev.map((item) => {
        if (item.id !== post.id) return item;
        return {
          ...item,
          reviewStatus: updated.reviewStatus,
          status: updated.status,
        };
      }));
      setSelectedPost((current) => {
        if (!current || current.id !== post.id) return current;
        return {
          ...current,
          reviewStatus: updated.reviewStatus,
          status: updated.status,
        };
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar revisão';
      setError(message);
    } finally {
      setDecisionLoadingId(null);
    }
  };

  const speciesOptions = [
    { value: '', label: 'Todas as espécies' },
    ...species.map((s) => ({ value: s.id.toString(), label: s.name })),
  ];

  const statusOptions = [
    { value: '', label: 'Todos os status de visibilidade' },
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'INACTIVE', label: 'Inativo' },
  ];

  const reviewStatusOptions = [
    { value: '', label: 'Todos os status de revisão' },
    { value: 'PENDING', label: 'Em revisão' },
    { value: 'APPROVED', label: 'Aprovado' },
    { value: 'REJECTED', label: 'Recusado' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1500px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 800,
          color: 'var(--foreground)',
          marginBottom: '8px',
          letterSpacing: '-0.5px',
        }}>
          Publicações Cadastradas
        </h1>
        <p style={{ color: 'var(--muted-foreground)', fontSize: '15px', fontWeight: 500 }}>
          <span style={{
            background: 'var(--secondary)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 600,
            marginRight: '8px',
          }}>
            {total}
          </span>
          publicaç{total !== 1 ? 'ões' : 'ão'} encontrada{total !== 1 ? 's' : ''}
        </p>
      </div>

      <div style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '28px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      }}>
        <h2 style={{ margin: 0, marginBottom: '10px', fontSize: '18px', fontWeight: 700 }}>Revisão de Novas Publicações</h2>
        <p style={{ marginTop: 0, marginBottom: '14px', color: 'var(--muted-foreground)', fontSize: '14px' }}>
          Controle se novas publicações precisam de aprovação manual antes de aparecer no app. Com aprovação manual ligada, será enviado e-mail para esta lista quando uma publicação entrar em revisão.
        </p>

        {settingsLoading ? (
          <div style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>Carregando configuração...</div>
        ) : (
          <>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                padding: '14px 16px',
                marginBottom: '18px',
                cursor: 'pointer',
                background: 'var(--muted)',
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontWeight: 700, color: 'var(--foreground)' }}>
                  Exigir aprovação manual
                </span>
                <span style={{ color: 'var(--muted-foreground)', fontSize: '13px' }}>
                  {manualApprovalEnabled
                    ? 'Novas publicações entram em revisão até um administrador aprovar.'
                    : 'Novas publicações ficam ativas automaticamente no app.'}
                </span>
              </span>
              <input
                type="checkbox"
                checked={manualApprovalEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setManualApprovalEnabled(e.target.checked);
                  setSettingsMessage(null);
                }}
                style={{ display: 'none' }}
              />
              <span
                aria-hidden="true"
                style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '999px',
                  background: manualApprovalEnabled ? 'var(--success)' : 'var(--border)',
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'background 0.2s ease',
                }}
              >
                <span
                  style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: '#fff',
                    position: 'absolute',
                    top: '2px',
                    left: manualApprovalEnabled ? '24px' : '2px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                    transition: 'left 0.2s ease',
                  }}
                />
              </span>
            </label>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
              <Input
                label="Adicionar e-mail"
                value={emailInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailInput(e.target.value)}
                placeholder="exemplo@dominio.com"
              />
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button onClick={addEmailToList}>Adicionar</Button>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
              {reviewEmails.length === 0 ? (
                <span style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>Nenhum e-mail configurado.</span>
              ) : (
                reviewEmails.map((email) => (
                  <span
                    key={email}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'var(--muted)',
                      borderRadius: '999px',
                      padding: '6px 10px',
                      fontSize: '13px',
                    }}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeEmailFromList(email)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: 'var(--muted-foreground)',
                        fontWeight: 700,
                      }}
                    >
                      x
                    </button>
                  </span>
                ))
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button onClick={saveReviewSettings} isLoading={settingsSaving}>Salvar configuração</Button>
              {settingsMessage && (
                <span style={{ fontSize: '13px', color: settingsMessage.includes('sucesso') ? 'var(--success)' : 'var(--destructive)' }}>
                  {settingsMessage}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '28px',
        marginBottom: '28px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '24px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--secondary)" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 700,
            color: 'var(--foreground)',
            margin: 0,
          }}>
            Filtros de Pesquisa
          </h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '16px',
        }}>
          <Select
            label="Espécie Animal"
            value={filters.speciesId?.toString() || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFilters({
                ...filters,
                speciesId: e.target.value ? Number(e.target.value) : undefined,
                page: 1,
              })
            }
            options={speciesOptions}
          />

          <Input
            label="Estado"
            value={filters.region || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, region: e.target.value, page: 1 })
            }
            placeholder="Ex: São Paulo"
          />

          <Input
            label="Cidade"
            value={filters.city || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, city: e.target.value, page: 1 })
            }
            placeholder="Ex: Caçador"
          />

          <Input
            label="Data Inicial"
            type="date"
            value={filters.startDate || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, startDate: e.target.value, page: 1 })
            }
          />

          <Input
            label="Data Final"
            type="date"
            value={filters.endDate || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, endDate: e.target.value, page: 1 })
            }
          />

          <Select
            label="Visibilidade"
            value={filters.status || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFilters({
                ...filters,
                status: e.target.value ? (e.target.value as PostStatus) : undefined,
                page: 1,
              })
            }
            options={statusOptions}
          />

          <Select
            label="Revisão"
            value={filters.reviewStatus || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFilters({
                ...filters,
                reviewStatus: e.target.value ? (e.target.value as PostReviewStatus) : undefined,
                page: 1,
              })
            }
            options={reviewStatusOptions}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button onClick={handleSearch} isLoading={loading}>
            Pesquisar
          </Button>
          <Button variant="secondary" onClick={handleClearFilters}>
            Limpar Filtros
          </Button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '16px',
          background: 'var(--destructive)',
          color: 'white',
          borderRadius: '8px',
          marginBottom: '24px',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '24px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        }}>
          <TableSkeleton rows={10} columns={9} />
        </div>
      ) : (
        <>
          <div style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(to bottom, var(--muted) 0%, var(--card) 100%)',
                    borderBottom: '2px solid var(--border)',
                  }}>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ID</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nome</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Espécie</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cidade</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Usuário</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Revisão</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Visibilidade</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Criado em</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          padding: '64px',
                          textAlign: 'center',
                          color: 'var(--muted-foreground)',
                          fontSize: '15px',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3">
                            <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                          <span>Nenhuma publicação encontrada</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    posts.map((post, index) => {
                      const reviewStatus = normalizeReviewStatus(post);
                      const pendingAction = decisionLoadingId === post.id;

                      return (
                        <tr
                          key={post.id}
                          style={{
                            background: index % 2 === 0 ? 'transparent' : 'var(--muted)',
                            borderBottom: '1px solid var(--border)',
                          }}
                        >
                          <td style={{ padding: '16px 20px' }}>
                            <span style={{
                              background: 'var(--secondary)',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}>
                              #{post.id}
                            </span>
                          </td>
                          <td style={{ padding: '16px 20px', fontWeight: 600, fontSize: '15px' }}>{getPostDisplayName(post)}</td>
                          <td style={{ padding: '16px 20px' }}>
                            <span style={{
                              background: 'var(--info-bg)',
                              color: 'var(--info)',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 500,
                            }}>
                              {post.animalSpecies?.name || '-'}
                            </span>
                          </td>
                          <td style={{ padding: '16px 20px', color: 'var(--muted-foreground)' }}>{post.locationCity}</td>
                          <td style={{ padding: '16px 20px' }}>{post.user?.name || '-'}</td>
                          <td style={{ padding: '16px 20px' }}>{getReviewBadge(reviewStatus)}</td>
                          <td style={{ padding: '16px 20px' }}>{getVisibilityBadge(post.status)}</td>
                          <td style={{ padding: '16px 20px', fontSize: '13px' }}>{formatDate(post.createdAt)}</td>
                          <td style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => setSelectedPost(post)}
                                style={{
                                  border: '1px solid var(--border)',
                                  borderRadius: '8px',
                                  padding: '8px 10px',
                                  cursor: 'pointer',
                                  background: 'var(--card)',
                                  color: 'var(--foreground)',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                }}
                              >
                                Ver detalhes
                              </button>

                              {reviewStatus === 'PENDING' ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleReviewDecision(post, 'APPROVED')}
                                    disabled={pendingAction}
                                    style={{
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '8px 10px',
                                      cursor: pendingAction ? 'not-allowed' : 'pointer',
                                      background: '#1f7a3f',
                                      color: '#fff',
                                      fontWeight: 600,
                                      fontSize: '12px',
                                      opacity: pendingAction ? 0.7 : 1,
                                    }}
                                  >
                                    Aprovar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleReviewDecision(post, 'REJECTED')}
                                    disabled={pendingAction}
                                    style={{
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '8px 10px',
                                      cursor: pendingAction ? 'not-allowed' : 'pointer',
                                      background: '#b42318',
                                      color: '#fff',
                                      fontWeight: 600,
                                      fontSize: '12px',
                                      opacity: pendingAction ? 0.7 : 1,
                                    }}
                                  >
                                    Recusar
                                  </button>
                                </>
                              ) : reviewStatus === 'APPROVED' ? (
                                <button
                                  type="button"
                                  onClick={() => handleReviewDecision(post, 'REJECTED')}
                                  disabled={pendingAction}
                                  style={{
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 10px',
                                    cursor: pendingAction ? 'not-allowed' : 'pointer',
                                    background: '#b42318',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    opacity: pendingAction ? 0.7 : 1,
                                  }}
                                >
                                  Bloquear
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleReviewDecision(post, 'APPROVED')}
                                  disabled={pendingAction}
                                  style={{
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '8px 10px',
                                    cursor: pendingAction ? 'not-allowed' : 'pointer',
                                    background: '#1f7a3f',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    opacity: pendingAction ? 0.7 : 1,
                                  }}
                                >
                                  Liberar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={filters.page || 1}
              totalPages={totalPages}
              onPageChange={(page) => setFilters({ ...filters, page })}
              totalItems={total}
              itemsPerPage={filters.limit || DEFAULT_PAGE_SIZE}
            />
          )}
        </>
      )}

      {selectedPost && (
        <div
          onClick={() => setSelectedPost(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '980px',
              maxHeight: '90vh',
              overflow: 'auto',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '14px',
              padding: '20px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '22px', color: 'var(--foreground)' }}>
                Verificação da Publicação #{selectedPost.id}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedPost(null)}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  background: 'var(--muted)',
                  padding: '8px 10px',
                  cursor: 'pointer',
                }}
              >
                Fechar
              </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
              {getReviewBadge(normalizeReviewStatus(selectedPost))}
              {getVisibilityBadge(selectedPost.status)}
            </div>

            <div style={{ marginBottom: '18px' }}>
              <h4 style={{ margin: 0, marginBottom: '8px', fontSize: '15px' }}>Imagens</h4>
              {selectedPost.postImages && selectedPost.postImages.length > 0 ? (
                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '6px' }}>
                  {selectedPost.postImages.map((image) => (
                    <div key={image.id} style={{ minWidth: '180px' }}>
                      <img
                        src={getImageUrl(image.url)}
                        alt={`Post ${selectedPost.id}`}
                        style={{
                          width: '180px',
                          height: '180px',
                          objectFit: 'cover',
                          borderRadius: '10px',
                          border: image.main ? '3px solid #1f7a3f' : '1px solid var(--border)',
                        }}
                      />
                      <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--muted-foreground)' }}>
                        {image.main ? 'Imagem principal' : 'Imagem secundária'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>Nenhuma imagem.</div>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '12px',
              marginBottom: '14px',
            }}>
              <div><strong>Identificação:</strong> {getPostDisplayName(selectedPost)}</div>
              <div><strong>Tipo:</strong> {getPostTypeLabel(selectedPost.type)}</div>
              <div><strong>Espécie:</strong> {selectedPost.animalSpecies?.name || '-'}</div>
              <div><strong>Raça:</strong> {selectedPost.animalSpeciesBreed?.name || '-'}</div>
              <div><strong>Porte:</strong> {selectedPost.animalSpeciesSize?.name || '-'}</div>
              <div><strong>Sexo:</strong> {getSexLabel(selectedPost.sex)}</div>
              <div><strong>Cor:</strong> {selectedPost.color || '-'}</div>
              <div><strong>Microchip:</strong> {selectedPost.microchip || '-'}</div>
              <div><strong>Cidade:</strong> {selectedPost.locationCity || '-'}</div>
              <div><strong>Estado:</strong> {selectedPost.locationRegion || '-'}</div>
              <div><strong>Rua:</strong> {selectedPost.locationStreet || '-'}</div>
              <div><strong>Criado em:</strong> {formatDate(selectedPost.createdAt)}</div>
              <div><strong>Usuário:</strong> {selectedPost.user?.name || '-'}</div>
              <div><strong>Email do usuário:</strong> {selectedPost.user?.email || '-'}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: 0, marginBottom: '6px', fontSize: '15px' }}>Descrição</h4>
              <div style={{
                background: 'var(--muted)',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '14px',
                lineHeight: 1.5,
              }}>
                {selectedPost.description || '-'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {normalizeReviewStatus(selectedPost) === 'PENDING' ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleReviewDecision(selectedPost, 'APPROVED')}
                    disabled={decisionLoadingId === selectedPost.id}
                    style={{
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      cursor: decisionLoadingId === selectedPost.id ? 'not-allowed' : 'pointer',
                      background: '#1f7a3f',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '13px',
                      opacity: decisionLoadingId === selectedPost.id ? 0.7 : 1,
                    }}
                  >
                    Aprovar publicação
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReviewDecision(selectedPost, 'REJECTED')}
                    disabled={decisionLoadingId === selectedPost.id}
                    style={{
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      cursor: decisionLoadingId === selectedPost.id ? 'not-allowed' : 'pointer',
                      background: '#b42318',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '13px',
                      opacity: decisionLoadingId === selectedPost.id ? 0.7 : 1,
                    }}
                  >
                    Recusar publicação
                  </button>
                </>
              ) : normalizeReviewStatus(selectedPost) === 'APPROVED' ? (
                <button
                  type="button"
                  onClick={() => handleReviewDecision(selectedPost, 'REJECTED')}
                  disabled={decisionLoadingId === selectedPost.id}
                  style={{
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    cursor: decisionLoadingId === selectedPost.id ? 'not-allowed' : 'pointer',
                    background: '#b42318',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13px',
                    opacity: decisionLoadingId === selectedPost.id ? 0.7 : 1,
                  }}
                >
                  Bloquear publicação
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleReviewDecision(selectedPost, 'APPROVED')}
                  disabled={decisionLoadingId === selectedPost.id}
                  style={{
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    cursor: decisionLoadingId === selectedPost.id ? 'not-allowed' : 'pointer',
                    background: '#1f7a3f',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13px',
                    opacity: decisionLoadingId === selectedPost.id ? 0.7 : 1,
                  }}
                >
                  Liberar publicação
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
